import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Drag and drop for the admin lists. The order the rows are left in is saved
// as they are dropped, so there is no separate save button to forget, and the
// result comes back as a message in the corner — which is also the live region
// that says it out loud for anyone who cannot see the rows move.
//
// A page can hold more than one list, and they are separate: a row belongs to
// the list it started in and cannot be dragged into the other one. Dragging
// needs a pointer, so every row also carries move buttons that post the same
// change over a plain form; those work with this controller switched off.
export default class extends Controller {
  static targets = ["list", "toasts", "savedToast", "failedToast"]
  static values = { url: String }

  connect() {
    this.sortables = this.listTargets.map((list) => Sortable.create(list, {
      draggable: "li",
      handle: "[data-sortable-handle]",
      animation: 150,
      ghostClass: "ad-row--ghost",
      dragClass: "ad-row--drag",
      onEnd: () => this.save(list)
    }))
  }

  disconnect() {
    this.sortables?.forEach((sortable) => sortable.destroy())
    this.sortables = null
  }

  save(list) {
    const body = new FormData()
    this.rows(list).forEach((row) => body.append("ids[]", row.dataset.id))

    fetch(this.urlValue, {
      method: "PATCH",
      headers: { "X-CSRF-Token": this.csrfToken },
      body
    }).then((response) => {
      if (!response.ok) throw new Error(response.status)
      this.markEnds(list)
      this.toast(this.savedToastTarget)
    }).catch(() => {
      this.toast(this.failedToastTarget)
    })
  }

  // One message at a time. Dropping three rows in a row is one piece of news,
  // not three, so the message already standing is replaced rather than stacked
  // on. The clone carries the flash controller with it, which is what makes it
  // take itself away again.
  toast(template) {
    this.toastsTarget.replaceChildren(template.content.cloneNode(true))
  }

  // The first row cannot move up and the last cannot move down. The page was
  // rendered with that already worked out, and a drop changes which rows those
  // are, so the buttons are brought back in step here.
  markEnds(list) {
    const rows = this.rows(list)

    rows.forEach((row, index) => {
      this.moveButton(row, "up").disabled = index === 0
      this.moveButton(row, "down").disabled = index === rows.length - 1
    })
  }

  rows(list) {
    return Array.from(list.children)
  }

  moveButton(row, direction) {
    return row.querySelector(`[data-sortable-move="${direction}"] button`)
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }
}
